'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import React from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Button } from '../ui/button';
import { formatBlogPublishedDate } from '../../utils/blog-date';

export type BlogCardProps = {
  blog: Blog;
  firstBlog?: boolean;
};

export function BlogCard({ blog, firstBlog = false }: BlogCardProps) {
  const Title = firstBlog ? 'h2' : 'h3';
  const textContent = getTextFromHTML(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();

  const formattedDate = formatBlogPublishedDate(blog.created_at, locale);

  return (
    <Link
      prefetch={false}
      href={blog.slug}
      className={cn(
        'flex w-full flex-row items-center justify-start gap-4 md:gap-8',
        firstBlog && 'pr-6',
      )}
    >
      <Image
        src={blog.image?.url || '/images/placeholder.png'}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        width={0}
        height={0}
        loading={firstBlog ? 'eager' : 'lazy'}
        fetchPriority={firstBlog ? 'high' : undefined}
        className={cn(
          'w-full overflow-hidden rounded-[8px] object-cover object-center sm:h-[240px]',
          firstBlog
            ? 'h-[200px] sm:h-[300px] md:h-[385px]'
            : 'h-[200px] w-full min-w-[165px] max-w-[165px] sm:aspect-square sm:h-auto lg:min-w-[220px] lg:max-w-[220px] xl:min-w-[260px] xl:max-w-[260px]',
        )}
        sizes={firstBlog ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 768px) 25vw, 50vw'}
      />
      <div
        className={cn(
          'flex w-full flex-col items-start justify-start gap-2',
          firstBlog ? 'max-w-[526px]' : 'max-w-[278px]',
        )}
      >
        {blog.categories.length > 0 && (
          <span className="rounded-full bg-[#EAD7D6] px-3 py-[6px] text-xs text-primary">
            {blog.categories[0]?.name}
          </span>
        )}
        <Title
          data-ea-visual-tag="h3"
          className={cn(
            'line-clamp-2 text-lg font-normal text-foreground',
            firstBlog ? 'sm:text-xl md:text-2xl lg:text-3xl' : 'sm:text-lg md:text-xl lg:text-2xl',
          )}
        >
          {blog.name}
        </Title>
        <HtmlRenderer
          html={textContent}
          className={cn(
            'line-clamp-1 w-full text-left text-[#838383]',
            firstBlog ? 'sm:line-clamp-3' : 'sm:line-clamp-2',
          )}
        />
        <p className={cn('text-xs font-normal text-[#838383]', !firstBlog && 'mt-4')}>
          {formattedDate}
        </p>
        {firstBlog && (
          <Button asChild className="mt-4 hidden md:flex">
            <span data-ea-visual-tag="button">{t('text1148')}</span>
          </Button>
        )}
      </div>
    </Link>
  );
}
